import type { DeviceSpecDetail, DeviceSpecDevice, DeviceSpecTag, SpecMachine, SpecMachineItem } from '../../types'

// 명세서 공동 편집 문서 — 변경(SpecOp) 만들기·반영하기. 서버(DeviceSpecService.ApplyAsync)와 같은 규칙이어야 모든 화면이 같아진다.
//  · 변경은 항목(명세서 정보·디바이스·태그·인터페이스·전송코드)의 바뀐 칸만 — 같은 행의 다른 칸을 둘이 고치면 둘 다 남는다.
//  · 자리는 "어느 항목 뒤"(at) — 동시에 행을 넣어도 안 사라진다.
//  · Change = 되돌리기 단위 (바뀐 칸의 이전·이후 값)
//  · 부모(디바이스·인터페이스)와 자식(태그·인터페이스 항목) 두 단계 — 자식 변경의 device 는 부모 키

export type Draft = {
  name: string
  companyId: string
  memo: string
  devices: DeviceSpecDevice[]
  machines: SpecMachine[]
}

export const toDraft = (s: DeviceSpecDetail): Draft => ({
  name: s.name, companyId: s.company?.id ?? '', memo: s.memo, devices: s.devices, machines: s.master?.machines ?? [],
})

/** 저장되는(Edge 로 가는) 태그인지 — 주소가 있거나 주소 칸에 수식이 있다 */
export const hasAddress = (t: DeviceSpecTag) => t.address.trim() !== '' || !!t.formulas?.address?.trim()

// ── 변경 ──

export type ParentKind = 'device' | 'machine'
export type ChildKind = 'tag' | 'mitem'
export type Kind = 'meta' | ParentKind | ChildKind
export type SpecOp = { kind: Kind; key: string; device?: string | null; at?: string | null; remove?: boolean; value?: Record<string, unknown> | null }

const META_FIELDS = ['name', 'companyId', 'memo'] as const
const DEVICE_FIELDS = ['category', 'maker', 'model', 'code', 'name', 'description', 'channelType', 'channel', 'memo', 'interfaceId'] as const
const TAG_FIELDS = ['name', 'address', 'size', 'dataType', 'arithmetic', 'description', 'alias', 'formulas'] as const
const MACHINE_FIELDS = ['code', 'name', 'description'] as const
const MITEM_FIELDS = ['code', 'description', 'device', 'address'] as const

type Node = { key: string }
type ParentNode = Node & Record<string, unknown>

// 부모 종류별 — 문서의 목록, 자식 목록 칸, 자식 종류, 칸, 빈 값
interface ParentSpec {
  list: 'devices' | 'machines'
  children: 'tags' | 'items'
  child: ChildKind
  fields: readonly string[]
  childFields: readonly string[]
  blank: (key: string) => ParentNode
  blankChild: (key: string) => ParentNode
}

const blankDevice = (key: string): DeviceSpecDevice => ({
  key, category: '', maker: '', model: '', code: '', name: '', description: '', channelType: '', channel: {}, memo: '', interfaceId: '', tags: [],
})
const blankTag = (key: string): DeviceSpecTag => ({
  key, name: '', address: '', size: '', dataType: '', arithmetic: '', description: '', alias: '', formulas: {},
})
export const blankMachine = (key: string): SpecMachine => ({ key, code: '', name: '', description: '', items: [] })
export const blankMachineItem = (key: string): SpecMachineItem => ({ key, code: '', description: '', device: '', address: '' })

const PARENTS: Record<ParentKind, ParentSpec> = {
  device: { list: 'devices', children: 'tags', child: 'tag', fields: DEVICE_FIELDS, childFields: TAG_FIELDS,
    blank: k => blankDevice(k) as unknown as ParentNode, blankChild: k => blankTag(k) as unknown as ParentNode },
  machine: { list: 'machines', children: 'items', child: 'mitem', fields: MACHINE_FIELDS, childFields: MITEM_FIELDS,
    blank: k => blankMachine(k) as unknown as ParentNode, blankChild: k => blankMachineItem(k) as unknown as ParentNode },
}
const PARENT_OF: Record<ChildKind, ParentKind> = { tag: 'device', mitem: 'machine' }
const isParent = (k: Kind): k is ParentKind => k in PARENTS
const isChild = (k: Kind): k is ChildKind => k in PARENT_OF

// 서버(DeviceSpecService.Place)와 똑같이 — at: null/undefined 면 그대로(새 항목이면 맨 끝), "" 면 맨 앞, 키면 그 뒤(없으면 맨 끝)
function place<T extends Node>(list: T[], item: T, at: string | null | undefined, isNew: boolean) {
  if (at == null && !isNew) return
  if (!isNew) list.splice(list.findIndex(x => x.key === item.key), 1)
  let i = list.length
  if (at === '') i = 0
  else if (at != null) { const j = list.findIndex(x => x.key === at); if (j >= 0) i = j + 1 }
  list.splice(i, 0, item)
}

function patchOf<T extends object>(item: T, value: Record<string, unknown> | null | undefined, fields: readonly string[]): T {
  if (!value) return item
  const out = { ...item } as Record<string, unknown>
  for (const f of fields) if (f in value) out[f] = value[f]
  return out as T
}

export function applyOps(doc: Draft, ops: SpecOp[]): Draft {
  if (ops.length === 0) return doc
  let meta = { name: doc.name, companyId: doc.companyId, memo: doc.memo }
  const lists: Record<ParentSpec['list'], ParentNode[]> = {
    devices: doc.devices as unknown as ParentNode[], machines: doc.machines as unknown as ParentNode[],
  }
  const copied = new Set<string>()
  const listOf = (spec: ParentSpec) => {
    if (!copied.has(spec.list)) { lists[spec.list] = [...lists[spec.list]]; copied.add(spec.list) }
    return lists[spec.list]
  }
  for (const op of ops) {
    if (op.kind === 'meta') {
      const v = op.value ?? {}
      meta = { ...meta, ...('name' in v ? { name: String(v.name ?? '') } : {}), ...('memo' in v ? { memo: String(v.memo ?? '') } : {}),
        ...('companyId' in v ? { companyId: (v.companyId as string | null) ?? '' } : {}) }
    } else if (isParent(op.kind)) {
      const spec = PARENTS[op.kind]
      const list = listOf(spec)
      const at = list.findIndex(d => d.key === op.key)
      if (op.remove) { if (at >= 0) list.splice(at, 1); continue }
      const isNew = at < 0
      const next = patchOf(isNew ? spec.blank(op.key) : list[at], op.value, spec.fields)
      if (!isNew) list[at] = next
      place(list, next, op.at, isNew)
    } else if (isChild(op.kind)) {
      const spec = PARENTS[PARENT_OF[op.kind]]
      const list = listOf(spec)
      const pi = list.findIndex(d => d.key === op.device)
      if (pi < 0) continue
      const children = [...(list[pi][spec.children] as ParentNode[])]
      const at = children.findIndex(t => t.key === op.key)
      if (op.remove) { if (at >= 0) children.splice(at, 1) }
      else {
        const isNew = at < 0
        const next = patchOf(isNew ? spec.blankChild(op.key) : children[at], op.value, spec.childFields)
        if (!isNew) children[at] = next
        place(children, next, op.at, isNew)
      }
      list[pi] = { ...list[pi], [spec.children]: children }
    }
  }
  return {
    ...meta,
    devices: lists.devices as unknown as DeviceSpecDevice[],
    machines: lists.machines as unknown as SpecMachine[],
  }
}

// ── 바뀐 것 찾기 (화면 상태 → 변경) ──

// 되돌리기 단위 — 항목 하나의 바뀐 칸들. whole = 만들기·지우기 (부모면 자식까지 통째로)
export type Change = {
  kind: Kind
  key: string
  device: string | null
  fields: string[]
  before: Record<string, unknown> | null
  after: Record<string, unknown> | null
  beforeAt: string | null // 자리를 옮겼을 때만 (앞 항목 키, "" = 맨 앞)
  afterAt: string | null
}

export const changeId = (c: { kind: Kind; key: string }) => `${c.kind}:${c.key}`
const sameValue = (a: unknown, b: unknown) => a === b || JSON.stringify(a ?? (typeof b === 'object' ? {} : '')) === JSON.stringify(b ?? (typeof a === 'object' ? {} : ''))

// 서로 다 있는 항목끼리 본 앞 항목 — 새로 넣거나 지운 행 때문에 이웃 행이 "옮겨졌다"고 보지 않게
function preds<T extends Node>(list: T[], keep: (key: string) => boolean): Map<string, string> {
  const out = new Map<string, string>()
  let prev = ''
  for (const x of list) if (keep(x.key)) { out.set(x.key, prev); prev = x.key }
  return out
}
const predIn = <T extends Node>(list: T[], i: number) => (i === 0 ? '' : list[i - 1].key)

function diffList<T extends Node>(kind: Kind, device: string | null, a: T[], b: T[], fields: readonly string[], whole: (x: T) => Record<string, unknown>, out: Change[]) {
  const before = new Map(a.map((x, i) => [x.key, { x, i }]))
  const after = new Set(b.map(x => x.key))
  const pa = preds(a, k => after.has(k)), pb = preds(b, k => before.has(k))
  b.forEach((x, i) => {
    const old = before.get(x.key)
    if (!old) {
      out.push({ kind, key: x.key, device, fields: [...fields], before: null, after: whole(x), beforeAt: null, afterAt: predIn(b, i) })
      return
    }
    if (old.x === x && pa.get(x.key) === pb.get(x.key)) return
    const changed = old.x === x ? [] : fields.filter(f => !sameValue((old.x as Record<string, unknown>)[f], (x as Record<string, unknown>)[f]))
    const moved = pa.get(x.key) !== pb.get(x.key)
    if (changed.length === 0 && !moved) return
    out.push({
      kind, key: x.key, device, fields: changed,
      before: pick(old.x, changed), after: pick(x, changed),
      beforeAt: moved ? predIn(a, old.i) : null, afterAt: moved ? predIn(b, i) : null,
    })
  })
  a.forEach((x, i) => {
    if (!after.has(x.key)) out.push({ kind, key: x.key, device, fields: [...fields], before: whole(x), after: null, beforeAt: predIn(a, i), afterAt: null })
  })
}

const pick = (x: object, fields: readonly string[]) => Object.fromEntries(fields.map(f => [f, (x as Record<string, unknown>)[f]]))

export function diff(a: Draft, b: Draft): Change[] {
  const out: Change[] = []
  const meta = META_FIELDS.filter(f => a[f] !== b[f])
  if (meta.length) out.push({ kind: 'meta', key: 'meta', device: null, fields: [...meta], before: pick(a, meta), after: pick(b, meta), beforeAt: null, afterAt: null })
  for (const kind of Object.keys(PARENTS) as ParentKind[]) {
    const spec = PARENTS[kind]
    const la = a[spec.list] as unknown as ParentNode[], lb = b[spec.list] as unknown as ParentNode[]
    if (la === lb) continue
    diffList(kind, null, la, lb, spec.fields, p => ({ ...pick(p, spec.fields), [spec.children]: p[spec.children] }), out)
    // 양쪽에 다 있는 부모의 자식 (새로 만들거나 지운 부모의 자식은 부모 변경에 통째로 들어 있다)
    const old = new Map(la.map(p => [p.key, p]))
    for (const p of lb) {
      const prev = old.get(p.key)
      if (prev && prev[spec.children] !== p[spec.children]) {
        diffList(spec.child, p.key, prev[spec.children] as ParentNode[], p[spec.children] as ParentNode[], spec.childFields, c => pick(c, spec.childFields), out)
      }
    }
  }
  return out
}

// 변경 → 보낼 Op. 부모를 통째로 만들 때는 자식도 하나씩 이어서
export function opsOf(c: Change, side: 'before' | 'after'): SpecOp[] {
  const v = c[side]
  const at = side === 'before' ? c.beforeAt : c.afterAt
  if (v == null) return [{ kind: c.kind, key: c.key, device: c.device, remove: true }]
  const created = (side === 'before' ? c.after : c.before) == null
  if (isParent(c.kind) && created) {
    const spec = PARENTS[c.kind]
    const { [spec.children]: children, ...fields } = v as Record<string, unknown>
    const list = (children ?? []) as ParentNode[]
    return [
      { kind: c.kind, key: c.key, at, value: fields },
      ...list.map((t, i) => ({ kind: spec.child, key: t.key, device: c.key, at: predIn(list, i), value: pick(t, spec.childFields) })),
    ]
  }
  return [{ kind: c.kind, key: c.key, device: c.device, at: at ?? undefined, value: c.fields.length ? pick(v, c.fields) : undefined }]
}

// 되돌릴 때 순서 — 부모를 먼저 살리고(자식이 붙을 곳), 지울 때는 자식 다음 부모
export const order = (c: Change) => (c.kind === 'meta' ? 0 : isParent(c.kind) ? (c.before ? 1 : 3) : 2)

// 같은 항목의 이어진 변경 — 처음 before 와 마지막 after, 바뀐 칸은 합친다
export function merge(prev: Change, next: Change): Change {
  // 이번에 만든 항목을 이어서 고침 / 만들었다 지움
  if (prev.before == null) {
    return next.after == null ? { ...prev, after: null, afterAt: null }
      : { ...prev, after: { ...prev.after, ...next.after }, afterAt: next.afterAt ?? prev.afterAt }
  }
  // 고치다가 지움 — 되돌리면 고치기 전 값으로 통째로 살린다
  if (next.after == null) return { ...next, before: { ...next.before, ...prev.before }, beforeAt: prev.beforeAt ?? next.beforeAt }
  return {
    ...next,
    fields: [...new Set([...prev.fields, ...next.fields])],
    before: { ...next.before, ...prev.before },
    after: { ...prev.after, ...next.after },
    beforeAt: prev.beforeAt ?? next.beforeAt,
    afterAt: next.afterAt ?? prev.afterAt,
  }
}
